import './style.scss';

import {
	autoUpdate,
	flip,
	FloatingFocusManager,
	FloatingList,
	FloatingNode,
	FloatingPortal,
	FloatingTree,
	offset,
	safePolygon,
	shift,
	size,
	useClick,
	useDismiss,
	useFloating,
	useFloatingNodeId,
	useFloatingParentNodeId,
	useFloatingTree,
	useHover,
	useInteractions,
	useListItem,
	useListNavigation,
	useMergeRefs,
	useRole,
	useTransitionStatus,
	useTypeahead,
} from '@floating-ui/react';
import type { ButtonHTMLAttributes, JSX } from 'react';
import { createContext, forwardRef, useContext, useEffect, useRef, useState } from 'react';

import { type btnSize, type btnType, TypeButtonComponent } from '../button';
import { AngleRightSVG, PulseSVG } from '../svgs';

export interface IOptionAction {
	label: string;
	action?: () => void;
	disabled?: boolean;
	/** Leading icon, rendered to the left of the label for fast scanning. */
	icon?: JSX.Element;
	/** Optional secondary line under the label (e.g. why an item is disabled). */
	description?: string;
	/** Destructive styling hook (red label + hover). */
	danger?: boolean;
	/** One level of nested submenu. When present, `action` is ignored. */
	children?: IOptionAction[];
}

export interface IActionComponent {
	title?: string;
	value?: string;
	actions?: IOptionAction[];
	load?: boolean;
	buttonType?: btnType;
	buttonSize?: btnSize;
	icon?: JSX.Element;
	className?: string;
	noTitle?: boolean;
	/**
	 * Eyebrow rendered inside the trigger before `title`, for a value-bearing trigger
	 * ("Timing · Every week"). Part of the visible text, so it is part of the accessible
	 * name too — no separate aria-label needed. Ignored with `noTitle`.
	 */
	prefix?: string;
	/**
	 * Accessible name for a `noTitle` (kebab) trigger. Defaults to the historical 'Open action
	 * menu', which every existing call site and its tests rely on — pass this to name the row the
	 * menu acts on, so twenty kebabs on a page are not twenty identical buttons.
	 */
	ariaLabel?: string;
}

// ─── Shared menu coordination context ──────────────────────────────────────
// Lets a (possibly nested) menu register its items with the parent list and
// share active-index / focus state. Mirrors Floating UI's canonical nested
// menu recipe, adapted to a data-driven (`actions[]`) API.
interface IMenuContext {
	getItemProps: (userProps?: ButtonHTMLAttributes<HTMLButtonElement>) => Record<string, unknown>;
	activeIndex: number | null;
	setActiveIndex: React.Dispatch<React.SetStateAction<number | null>>;
	setHasFocusInside: React.Dispatch<React.SetStateAction<boolean>>;
	isOpen: boolean;
}

const MenuContext = createContext<IMenuContext>({
	getItemProps: () => ({}),
	activeIndex: null,
	setActiveIndex: () => {},
	setHasFocusInside: () => {},
	isOpen: false,
});

// ─── Leaf menu item ─────────────────────────────────────────────────────────
interface IMenuItemProps {
	label: string;
	icon?: JSX.Element;
	description?: string;
	disabled?: boolean;
	danger?: boolean;
	onSelect?: () => void;
}

const MenuItem = forwardRef<HTMLButtonElement, IMenuItemProps>(
	({ label, icon, description, disabled, danger, onSelect }, forwardedRef) => {
		const menu = useContext(MenuContext);
		// Disabled items are excluded from typeahead by passing a null label.
		const item = useListItem({ label: disabled ? null : label });
		const tree = useFloatingTree();
		const isActive = item.index === menu.activeIndex;
		const ref = useMergeRefs([item.ref, forwardedRef]);

		return (
			<button
				ref={ref}
				type='button'
				role='menuitem'
				className={`ielect-action-menu__item ${danger ? 'is-danger' : ''}`}
				tabIndex={isActive ? 0 : -1}
				disabled={disabled}
				aria-disabled={disabled || undefined}
				data-active={isActive ? 'true' : 'false'}
				{...menu.getItemProps({
					onClick: () => {
						if (disabled) return;
						onSelect?.();
						// Collapse the whole tree once a leaf is chosen.
						tree?.events.emit('click');
					},
					onFocus: () => menu.setHasFocusInside(true),
				})}
			>
				{icon ? <span className='ielect-action-menu__item-icon'>{icon}</span> : null}
				<span className='ielect-action-menu__item-body'>
					<span className='ielect-action-menu__item-label'>{label}</span>
					{description ? <span className='ielect-action-menu__item-desc'>{description}</span> : null}
				</span>
			</button>
		);
	}
);
MenuItem.displayName = 'MenuItem';

// ─── Menu (root kebab OR nested submenu) ─────────────────────────────────────
type IMenuComponentProps = IActionComponent & {
	/** Nested submenu trigger label (ignored at root). */
	label?: string;
	/** Nested submenu trigger leading icon. */
	triggerIcon?: JSX.Element;
	disabled?: boolean;
	danger?: boolean;
};

const MenuComponent = forwardRef<HTMLButtonElement, IMenuComponentProps>(
	(
		{
			actions,
			title,
			load,
			buttonType,
			buttonSize,
			className,
			icon,
			noTitle,
			prefix,
			ariaLabel,
			label,
			triggerIcon,
			disabled,
			danger,
		},
		forwardedRef
	) => {
		const [isOpen, setIsOpen] = useState(false);
		const [hasFocusInside, setHasFocusInside] = useState(false);
		const [activeIndex, setActiveIndex] = useState<number | null>(null);

		const elementsRef = useRef<(HTMLButtonElement | null)[]>([]);
		const labelsRef = useRef<(string | null)[]>([]);

		const tree = useFloatingTree();
		const nodeId = useFloatingNodeId();
		const parentId = useFloatingParentNodeId();
		const isNested = parentId != null;

		const parent = useContext(MenuContext);
		const item = useListItem();

		const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
			nodeId,
			open: isOpen,
			onOpenChange: setIsOpen,
			placement: isNested ? 'right-start' : 'bottom-end',
			strategy: 'fixed',
			whileElementsMounted: autoUpdate,
			middleware: [
				offset(isNested ? { mainAxis: 4, alignmentAxis: -4 } : 6),
				flip({
					padding: 8,
					fallbackPlacements: isNested ? ['left-start'] : ['top-end', 'bottom-start', 'top-start'],
				}),
				shift({ padding: 8 }),
				size({
					padding: 8,
					apply({ availableHeight, elements }) {
						elements.floating.style.maxHeight = `${Math.max(160, availableHeight)}px`;
					},
				}),
			],
		});

		const hover = useHover(context, {
			enabled: isNested,
			delay: { open: 60 },
			handleClose: safePolygon({ blockPointerEvents: true }),
		});
		const click = useClick(context, {
			event: 'mousedown',
			toggle: !isNested,
			ignoreMouse: isNested,
		});
		const role = useRole(context, { role: 'menu' });
		const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true, bubbles: true });

		const disabledIndices = (actions ?? []).map((a, i) => (a.disabled && !a.children?.length ? i : -1)).filter((i) => i >= 0);

		const listNav = useListNavigation(context, {
			listRef: elementsRef,
			activeIndex,
			nested: isNested,
			onNavigate: setActiveIndex,
			loop: true,
			disabledIndices,
		});
		const typeahead = useTypeahead(context, {
			listRef: labelsRef,
			onMatch: isOpen ? setActiveIndex : undefined,
			activeIndex,
		});

		const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
			hover,
			click,
			role,
			dismiss,
			listNav,
			typeahead,
		]);
		const { isMounted, status } = useTransitionStatus(context, { duration: { open: 120, close: 90 } });

		// Close this menu when any leaf in the tree is clicked, and close sibling
		// submenus when another submenu under the same parent opens.
		useEffect(() => {
			if (!tree) return;
			const handleTreeClick = () => setIsOpen(false);
			const onSubMenuOpen = (event: { nodeId: string; parentId: string | null }) => {
				if (event.nodeId !== nodeId && event.parentId === parentId) {
					setIsOpen(false);
				}
			};
			tree.events.on('click', handleTreeClick);
			tree.events.on('menuopen', onSubMenuOpen);
			return () => {
				tree.events.off('click', handleTreeClick);
				tree.events.off('menuopen', onSubMenuOpen);
			};
		}, [tree, nodeId, parentId]);

		useEffect(() => {
			if (isOpen && tree) {
				tree.events.emit('menuopen', { parentId, nodeId });
			}
		}, [tree, isOpen, nodeId, parentId]);

		// Defensive: close if the trigger unmounts (e.g. virtualised rows recycle).
		useEffect(
			() => () => {
				setIsOpen(false);
			},
			[]
		);

		const referenceRef = useMergeRefs([refs.setReference, item.ref, forwardedRef]);
		const isActiveInParent = parent.activeIndex === item.index;

		const triggerProps = getReferenceProps(
			parent.getItemProps({
				onFocus: () => {
					setHasFocusInside(false);
					parent.setHasFocusInside(true);
				},
			})
		);

		const menuBody = (
			<MenuContext.Provider value={{ activeIndex, setActiveIndex, getItemProps, setHasFocusInside, isOpen }}>
				<FloatingList
					elementsRef={elementsRef}
					labelsRef={labelsRef}
				>
					{isMounted ? (
						<FloatingPortal>
							<FloatingFocusManager
								context={context}
								modal={false}
								initialFocus={isNested ? -1 : 0}
								returnFocus={!isNested}
							>
								<div
									ref={refs.setFloating}
									style={floatingStyles}
									data-status={status}
									data-submenu={isNested ? 'true' : undefined}
									className='ielect-action-menu'
									{...getFloatingProps()}
								>
									{actions?.map((a, i) =>
										a.children?.length ? (
											<MenuComponent
												key={i}
												label={a.label}
												triggerIcon={a.icon}
												disabled={a.disabled}
												danger={a.danger}
												actions={a.children}
											/>
										) : (
											<MenuItem
												key={i}
												label={a.label}
												icon={a.icon}
												description={a.description}
												disabled={a.disabled}
												danger={a.danger}
												onSelect={a.action}
											/>
										)
									)}
								</div>
							</FloatingFocusManager>
						</FloatingPortal>
					) : null}
				</FloatingList>
			</MenuContext.Provider>
		);

		// Nested submenu: the trigger looks like a menu item with a trailing chevron.
		if (isNested) {
			return (
				<FloatingNode id={nodeId}>
					<button
						ref={referenceRef}
						type='button'
						role='menuitem'
						className={`ielect-action-menu__item ielect-action-menu__item--submenu ${danger ? 'is-danger' : ''} ${
							isOpen ? 'is-open' : ''
						}`}
						tabIndex={isActiveInParent ? 0 : -1}
						disabled={disabled}
						aria-disabled={disabled || undefined}
						aria-haspopup='menu'
						aria-expanded={isOpen}
						data-active={isActiveInParent ? 'true' : 'false'}
						data-focus-inside={hasFocusInside ? 'true' : undefined}
						{...triggerProps}
					>
						{triggerIcon ? <span className='ielect-action-menu__item-icon'>{triggerIcon}</span> : null}
						<span className='ielect-action-menu__item-body'>
							<span className='ielect-action-menu__item-label'>{label}</span>
						</span>
						<AngleRightSVG className='ielect-action-menu__item-chevron' />
					</button>
					{menuBody}
				</FloatingNode>
			);
		}

		// Root: the kebab / labelled trigger.
		return (
			<FloatingNode id={nodeId}>
				<TypeButtonComponent className={`ielect-action ${isOpen ? 'is-open' : ''}`}>
					<button
						ref={referenceRef}
						title={title || 'Open action menu'}
						aria-label={noTitle ? (ariaLabel ?? 'Open action menu') : undefined}
						aria-haspopup='menu'
						className={`dropdown-toggle ielect-action-trigger ${buttonType || ''} ${buttonSize || ''} ${className || ''}`}
						type='button'
						style={{ fontSize: 'var(--font-size-xs)' }}
						{...triggerProps}
					>
						{!noTitle && prefix ? <span className='ielect-action-trigger__prefix'>{prefix}</span> : null}
						{noTitle ? '' : title || 'Action'}
						{load ? (
							<>
								&nbsp;&nbsp;
								<PulseSVG />
							</>
						) : null}
						{icon}
					</button>
					{menuBody}
				</TypeButtonComponent>
			</FloatingNode>
		);
	}
);
MenuComponent.displayName = 'MenuComponent';

/**
 * Accessible portaled action ("kebab") menu with optional leading icons,
 * descriptions and one level of nested submenu.
 *
 * The menu is rendered into a Floating UI portal at the document root so it is
 * never clipped by ancestor `overflow: auto/hidden` containers (tables,
 * right-panel scroll bodies, etc.). Positioning, viewport collision detection,
 * focus management, keyboard navigation (incl. ArrowRight/Left for submenus)
 * and dismissal are delegated to Floating UI.
 */
export const ActionComponent = (props: IActionComponent) => {
	const parentId = useFloatingParentNodeId();
	if (parentId === null) {
		return (
			<FloatingTree>
				<MenuComponent {...props} />
			</FloatingTree>
		);
	}
	return <MenuComponent {...props} />;
};
